/**
 * Knowledge Base Search Page - Public article search and reading
 * Epic 61: Admin Kozpont Dashboard (Story 61-6)
 */

import {
  feedbackKbArticle,
  getKbArticles,
  KB_CATEGORY_LABEL_KEYS,
  type KbArticle,
  type KbCategory,
} from '@/api/admin-center';
import { Button, Card, CardContent } from '@/components/ui';
import { useApiError } from '@/hooks/use-api-error';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';

// ============================================
// CONSTANTS
// ============================================

const CATEGORIES: KbCategory[] = ['BERLES', 'SZERVIZ', 'PENZUGY', 'RENDSZER', 'ALTALANOS'];

const CATEGORY_COLORS: Record<KbCategory, string> = {
  BERLES: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  SZERVIZ: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  PENZUGY: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  RENDSZER: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  ALTALANOS: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

// ============================================
// MAIN PAGE
// ============================================

export function KbSearchPage() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const [articles, setArticles] = useState<KbArticle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<KbCategory | null>(null);
  const [expandedArticleId, setExpandedArticleId] = useState<string | null>(null);
  const [feedbackGiven, setFeedbackGiven] = useState<Record<string, boolean>>({});

  const fetchArticles = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getKbArticles({
        q: searchQuery || undefined,
        category: selectedCategory ?? undefined,
        status: 'PUBLISHED',
      });
      setArticles(response.data);
    } catch (err) {
      // 49Y: code-alapú lokalizált hiba (KB_ARTICLE_NOT_FOUND — #896 BE); fallback loadFailed.
      const message = getErrorMessage(err, t('error.loadFailed'));
      setError(message);
      console.error('Error fetching KB articles:', err);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, selectedCategory, getErrorMessage, t]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      void fetchArticles();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchArticles]);

  const handleFeedback = async (articleId: string, helpful: boolean) => {
    try {
      await feedbackKbArticle(articleId, helpful);
      setFeedbackGiven(prev => ({ ...prev, [articleId]: true }));
      // Update local article data
      setArticles(prev =>
        prev.map(article => {
          if (article.id !== articleId) return article;
          return {
            ...article,
            helpfulCount: helpful ? article.helpfulCount + 1 : article.helpfulCount,
            notHelpfulCount: helpful ? article.notHelpfulCount : article.notHelpfulCount + 1,
          };
        })
      );
    } catch (err) {
      console.error('Error submitting feedback:', err);
    }
  };

  const toggleArticle = (articleId: string) => {
    setExpandedArticleId(prev => (prev === articleId ? null : articleId));
  };

  return (
    <div className="min-h-full kgc-bg">
      {/* Header */}
      <header className="kgc-card-bg border-b border-gray-200 dark:border-gray-700">
        <div className="mx-auto max-w-4xl px-6 py-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('kb.searchTitle')}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Keressen a cikkek kozott, vagy valasszon kategoriat
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-6 py-6">
        {/* Search input */}
        <div className="relative mb-6">
          <svg
            className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder="{t('kb.searchInKb')}"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-12 pr-4 text-gray-900 placeholder-gray-400 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
          />
        </div>

        {/* Category filter buttons */}
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory(null)}
            className={cn(
              'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
              selectedCategory === null
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            )}
          >
            Mind
          </button>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(prev => (prev === cat ? null : cat))}
              className={cn(
                'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                selectedCategory === cat
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              )}
            >
              {t(KB_CATEGORY_LABEL_KEYS[cat] ?? cat)}
            </button>
          ))}
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500 dark:text-gray-400">{t('kb.loadingArticles')}</div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
            <p className="text-sm text-red-600 dark:text-red-400">
              {t('common.error')}: {error}
            </p>
          </div>
        )}

        {/* Articles list */}
        {!isLoading && !error && (
          <div className="space-y-4">
            {articles.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <svg
                  className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
                <p className="text-gray-500 dark:text-gray-400">{t('kb.noResults')}</p>
                <p className="mt-1 text-sm text-gray-400 dark:text-gray-300">
                  {t('kb.tryOtherSearch')}
                </p>
              </div>
            ) : (
              articles.map(article => (
                <Card key={article.id} className="overflow-hidden">
                  {/* Article header - always visible */}
                  <button
                    onClick={() => toggleArticle(article.id)}
                    className="flex w-full items-start justify-between p-5 text-left"
                  >
                    <div className="flex-1">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span
                          className={cn(
                            'rounded-full px-2.5 py-0.5 text-xs font-medium',
                            CATEGORY_COLORS[article.category]
                          )}
                        >
                          {t(KB_CATEGORY_LABEL_KEYS[article.category] ?? article.category)}
                        </span>
                        {article.tags.map(tag => (
                          <span
                            key={tag}
                            className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {article.title}
                      </h3>
                      {expandedArticleId !== article.id && (
                        <p className="mt-1 line-clamp-2 text-sm text-gray-500 dark:text-gray-400">
                          {article.content.substring(0, 200)}
                          {article.content.length > 200 ? '...' : ''}
                        </p>
                      )}
                    </div>
                    <svg
                      className={cn(
                        'ml-4 mt-1 h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform',
                        expandedArticleId === article.id && 'rotate-180'
                      )}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Expanded content */}
                  {expandedArticleId === article.id && (
                    <CardContent className="border-t border-gray-200 dark:border-gray-700">
                      {/* Article content rendered as plain text (Markdown could be added later) */}
                      <div className="prose prose-sm max-w-none dark:prose-invert">
                        {article.content.split('\n').map((paragraph, index) => (
                          <p key={index} className="text-gray-700 dark:text-gray-300">
                            {paragraph}
                          </p>
                        ))}
                      </div>

                      {/* Feedback section */}
                      <div className="mt-6 border-t border-gray-200 pt-4 dark:border-gray-700">
                        {feedbackGiven[article.id] ? (
                          <p className="text-sm text-green-600 dark:text-green-400">
                            {t('kb.feedbackThanks')}
                          </p>
                        ) : (
                          <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {t('kb.wasHelpful')}
                            </span>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={e => {
                                  e.stopPropagation();
                                  void handleFeedback(article.id, true);
                                }}
                              >
                                Hasznos volt
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={e => {
                                  e.stopPropagation();
                                  void handleFeedback(article.id, false);
                                }}
                              >
                                Nem segitett
                              </Button>
                            </div>
                          </div>
                        )}
                        <div className="mt-2 flex items-center gap-4 text-xs text-gray-400 dark:text-gray-300">
                          <span>{article.helpfulCount} hasznos</span>
                          <span>{article.notHelpfulCount} nem segitett</span>
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
